import React, { useRef, useEffect } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-regular-svg-icons/faLink";
import { HorizontalScrollbar } from "../../../components/HorizontalScrollbar";

const LinkRowComponent = ({
    state: { items = [], selectedItemIndex },
    isEditMode,
    onChange,
    focused,
}) => {
    const containerRef = useRef(null);
    const scrollbarRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let isDragging = false;
        let startX = 0;
        let scrollLeftStart = 0;
        let moved = false; // Track if user actually dragged

        const onMouseDown = (e) => {
            isDragging = true;
            moved = false;
            startX = e.pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
            container.classList.add("cursor-grabbing");
            e.preventDefault();
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            const x = e.pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true; // small threshold to count as drag
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onMouseUp = () => {
            isDragging = false;
            container.classList.remove("cursor-grabbing");
        };

        const onTouchStart = (e) => {
            isDragging = true;
            moved = false;
            startX = e.touches[0].pageX - container.offsetLeft;
            scrollLeftStart = container.scrollLeft;
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;
            const x = e.touches[0].pageX - container.offsetLeft;
            const walk = x - startX;
            if (Math.abs(walk) > 2) moved = true;
            container.scrollLeft = scrollLeftStart - walk;
        };

        const onTouchEnd = () => (isDragging = false);

        // --- Prevent clicks on links if dragging ---
        const onClickCapture = (e) => {
            if (moved) {
                // prevent link clicks if dragging occurred
                e.preventDefault();
                e.stopPropagation();
            }
        };

        container.addEventListener("mousedown", onMouseDown);
        container.addEventListener("mousemove", onMouseMove);
        container.addEventListener("mouseup", onMouseUp);
        container.addEventListener("mouseleave", onMouseUp);
        container.addEventListener("touchstart", onTouchStart);
        container.addEventListener("touchmove", onTouchMove);
        container.addEventListener("touchend", onTouchEnd);

        container.addEventListener("click", onClickCapture, true); // capture phase

        return () => {
            container.removeEventListener("mousedown", onMouseDown);
            container.removeEventListener("mousemove", onMouseMove);
            container.removeEventListener("mouseup", onMouseUp);
            container.removeEventListener("mouseleave", onMouseUp);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
            container.removeEventListener("click", onClickCapture, true);
        };
    }, []);

    if (items.length === 0) {
        return (
            <div className="flex py-10 justify-center items-center w-full">
                <FontAwesomeIcon icon={faLink} size="6x" />
            </div>
        );
    }

    return (
        <div className="relative">
            <div
                className="overflow-hidden flex gap-x-[15px] lg:gap-x-[30px] items-start"
                ref={containerRef}
            >
                {items.map((item, index) => {
                    const { linkHeading, linkImage, linkHref } = item;
                    return (
                        <a
                            type="button"
                            href={linkHref}
                            className={cn(
                                "flex flex-col min-w-[200px] lg:min-w-[420px] flex-1",
                            )}
                            style={{
                                boxShadow:
                                    selectedItemIndex === index &&
                                    focused &&
                                    "0 0 0 3px red",
                            }}
                            onClick={(e) => {
                                if (isEditMode) {
                                    e.preventDefault();
                                    onChange({
                                        selectedItemIndex: index,
                                    });
                                }
                            }}
                            key={`link-row-${index}`}
                        >
                            <div className="flex flex-col relative gap-[15px]">
                                <div className="aspect-[420/300] bg-brand-grey rounded-[10px] relative">
                                    {linkImage && (
                                        <img
                                            src={linkImage}
                                            className="absolute top-0 left-0 w-full h-full object-cover rounded-[10px]"
                                            alt="Background"
                                        />
                                    )}
                                </div>

                                {linkHeading && (
                                    <h2
                                        className={cn(
                                            `!text-[18px] !leading-[24px] lg:!text-[30px] lg:!leading-[36px] text-brand-black mb-0`,
                                        )}
                                    >
                                        {linkHeading}
                                    </h2>
                                )}
                            </div>
                        </a>
                    );
                })}
            </div>

            <HorizontalScrollbar
                containerRef={containerRef}
                ref={scrollbarRef}
            />
        </div>
    );
};

export default LinkRowComponent;
